import { MaskedOutlinedField, OutlinedFieldProps } from '@/components/common/field/MaskedOutlinedField';
import { toRussianNationalDigits, trackRussianPhone } from '@/utils/phone-utils';
import { withMaskedValue } from '@/components/common/field/MaskInput';
import React, { ChangeEvent, FC } from 'react';

/**
 * Fixed Russian phone mask; the '+7 (' prefix consists of mask literals,
 * so the mask engine keeps it in place and the user can never delete it.
 */
const PHONE_MASK = '+7 (999) 999-99-99';

/**
 * Placeholder character for unfilled mask slots.
 */
const PHONE_MASK_PLACEHOLDER = '_';

/**
 * Mask props are provided internally, so they are hidden from consumers.
 */
type Props = Omit<OutlinedFieldProps, 'mask' | 'maskPlaceholder' | 'alwaysShowMask' | 'track'>;

/**
 * The phone field of the profile phone dialog, and nothing else.
 * Every other phone field uses `PhoneNumberField`, which takes a number in any notation:
 * the Russia-only rule governs the number bound to the account,
 * not a contact number given while writing a letter or ordering a card.
 * Pair it with `russianPhoneFieldSchema`, which applies the same restriction to the value.
 */
export const RussianPhoneNumberField: FC<Props> = (props: Props) => {
  const { value, onChange, name, ...rest } = props;

  const handleChange: NonNullable<Props['onChange']> = (event) => {
    if (onChange) {
      const national: string = toRussianNationalDigits((event.target as HTMLInputElement).value);
      onChange(withMaskedValue(event as ChangeEvent<HTMLInputElement>, national ? '+7' + national : '', name));
    }
  };

  return (
    <MaskedOutlinedField
      {...rest}
      name={name}
      value={toRussianNationalDigits(String(value ?? ''))}
      onChange={handleChange}
      mask={PHONE_MASK}
      maskPlaceholder={PHONE_MASK_PLACEHOLDER}
      alwaysShowMask={true}
      track={trackRussianPhone}
    />
  );
};
